import clsx from "clsx";
import React, { Fragment, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { partTypeTire } from "../../constants/partTypes";
import { useAllowPartPricing } from "../../hooks/useAllowPartPricing";
import { TirePartItem } from "../../queries/useSearchTiresByVehicle";
import { useNullableDealerAccountInfo } from "../../stores/hooks/useNullableDealerAccountInfo";
import { AnyOrderItem, AnyPartItem } from "../../stores/slices/cartSlice";
import { KeyValue } from "../../ui/KeyValue/KeyValue";
import { Placeholder } from "../../ui/Placeholder";
import { PriceDisplay } from "../../ui/PriceDisplay/PriceDisplay";
import { getMsrp } from "../../util/getMsrp";
import { getTiresizeAndClass } from "../../util/getTireClassAndSize";
import { QuantitySelect } from "../QuantitySelect/QuantitySelect";
import { getDefaultQuantity } from "./getDefaultQuantity";
import { PricingOption } from "./getFlatPricingOptions";
import { PriceSelectEntry } from "./usePriceSelectState";
import { usePricing } from "./usePricing";
import { usePricingLabelOverride } from "./usePricingLabelOverride";

type PriceSelectProps = {
  busy?: boolean;
  disabled?: boolean;
  isStaggered: boolean;
  itemLabourClassName?: string;
  itemPriceClassName?: string;
  itemTireSizeClassName?: string;
  keyClassName?: string;
  msrpClassName?: string;
  onChange?: (pricingOption: PriceSelectEntry[]) => void;
  onReady?: (pricingOption: PriceSelectEntry[]) => void;
  orderItem: AnyOrderItem | null;
  part: AnyPartItem | null;
  promotionClassName?: string;
  quantityClassName?: string;
  quantityInputClassName?: string;
  quantityKeyClassName?: string;
  rowClassName?: string;
  ruleClassName?: string;
  savingsClassName?: string;
  shouldDisplayRule?: boolean;
  tireSizeKeyClassName?: string;
  totalClassName?: string;
  valueClassName?: string;
};

const getSizeLabel = (isStaggered: boolean, index: number): string => {
  if (isStaggered) {
    return index === 0 ? "priceSelect.front" : "priceSelect.rear";
  }
  return "searchResults.tireSize";
};

export const PriceSelect: FunctionComponent<PriceSelectProps> = ({
  busy,
  disabled,
  isStaggered,
  itemLabourClassName,
  itemPriceClassName,
  itemTireSizeClassName,
  keyClassName,
  msrpClassName,
  onChange,
  onReady,
  orderItem,
  part,
  promotionClassName,
  quantityClassName,
  quantityInputClassName,
  quantityKeyClassName,
  rowClassName,
  ruleClassName = "my-2",
  savingsClassName,
  shouldDisplayRule,
  tireSizeKeyClassName,
  totalClassName,
  valueClassName,
}) => {
  const { t } = useTranslation();

  const { entries, total } = usePricing({
    defaultQuantity: getDefaultQuantity({ isStaggered, orderItem, part }),
    isStaggered,
    onReady,
    part,
  });

  const dealer = useNullableDealerAccountInfo();

  const primaryPriceOption: PricingOption | null = entries[0]?.[1] ?? null;

  const allowPricing = useAllowPartPricing(part, primaryPriceOption);

  const pricingLabelOverride = usePricingLabelOverride(part);

  if (!allowPricing) {
    return t("cart.contactDealerForPricing");
  }

  const shouldDisplayPromotion: boolean =
    Boolean(primaryPriceOption) &&
    primaryPriceOption.label !== primaryPriceOption.quantity;

  const savings = Number(primaryPriceOption?.context.displaySavings) || 0;

  // NOTE: V1 logic is at: code/client/components/product/ProductTile.jsx:4212
  //
  // TW-1029: V1 also hid MSRP when dealer financing was enabled, because the
  // tile reserved that slot for a financing badge. The redesign never renders
  // that badge, so `enableFinancing` must not suppress MSRP.
  const shouldDisplayMsrp = Boolean(
    !isStaggered && dealer?.dealerEnableShowMsrpForItems
  );

  const shouldDisplaySavings =
    (shouldDisplayPromotion || shouldDisplayMsrp) && savings > 0;

  return (
    <>
      {entries.map(
        ([combinedPart, selectedPriceOption, setPricingIndex], key) => {
          const displayLabour =
            Number(selectedPriceOption.context.displayLabour) || 0;
          return (
            <Fragment key={combinedPart.itemId}>
              {key > 0 && <div className="w-full h-1" />}

              {shouldDisplayPromotion && (
                <div
                  className={clsx(
                    rowClassName,
                    promotionClassName,
                    "truncate font-light"
                  )}
                  title={selectedPriceOption.label}
                >
                  <span className={keyClassName}>
                    {selectedPriceOption.label}
                  </span>
                </div>
              )}

              {combinedPart.itemType === partTypeTire && (
                <>
                  <KeyValue
                    className={clsx(rowClassName, itemTireSizeClassName)}
                    keyClassName={clsx(keyClassName, tireSizeKeyClassName)}
                    valueClassName={valueClassName}
                  >
                    <>{t(getSizeLabel(isStaggered, key))}:</>
                    <>
                      {getTiresizeAndClass(part as TirePartItem, key > 0)}{" "}
                      {isStaggered && <span>#{combinedPart.partNumber}</span>}
                    </>
                  </KeyValue>
                </>
              )}

              <KeyValue
                className={clsx(rowClassName, itemPriceClassName)}
                keyClassName={keyClassName}
                valueClassName={valueClassName}
              >
                <>{t(pricingLabelOverride || "priceSelect.price")}:</>
                <Placeholder className="w-[4em]" loading={!part}>
                  <PriceDisplay>
                    {selectedPriceOption.context.displayPrice}
                  </PriceDisplay>
                </Placeholder>
              </KeyValue>

              {Boolean(displayLabour) && (
                <KeyValue
                  className={clsx(rowClassName, itemLabourClassName)}
                  keyClassName={keyClassName}
                  valueClassName={valueClassName}
                >
                  <>{t("searchResults.installation")}:</>
                  <Placeholder className="w-[4em]" loading={!part}>
                    <PriceDisplay>
                      {displayLabour * Number(selectedPriceOption.quantity)}
                    </PriceDisplay>
                  </Placeholder>
                </KeyValue>
              )}

              {shouldDisplayMsrp && (
                <KeyValue className={msrpClassName}>
                  <Placeholder className="w-[3em]" loading={!part}>
                    {t("searchResults.msrp")}:
                  </Placeholder>
                  <Placeholder className="w-[3.6em]" loading={!part}>
                    <PriceDisplay>{getMsrp(part)}</PriceDisplay>
                  </Placeholder>
                </KeyValue>
              )}

              <QuantitySelect
                busy={busy}
                className={clsx(rowClassName, quantityClassName)}
                disabled={disabled}
                entries={entries}
                entryIndex={key}
                keyClassName={clsx(keyClassName, quantityKeyClassName)}
                onChange={onChange}
                part={part}
                pricingOptions={combinedPart.pricingOptions}
                quantityInputClassName={quantityInputClassName}
                selectedPriceOption={selectedPriceOption}
                setPricingIndex={setPricingIndex}
                valueClassName={valueClassName}
              />
            </Fragment>
          );
        }
      )}
      {shouldDisplayRule && <hr className={ruleClassName} />}
      <KeyValue
        className={clsx(rowClassName, totalClassName)}
        keyClassName={keyClassName}
        valueClassName={valueClassName}
      >
        <>{t("priceSelect.total")}:</>
        <Placeholder className="w-[3em]" loading={!part}>
          <PriceDisplay>{total}</PriceDisplay>
        </Placeholder>
      </KeyValue>
      {shouldDisplaySavings && (
        <KeyValue
          className={clsx(rowClassName, savingsClassName)}
          keyClassName={keyClassName}
          valueClassName={clsx(valueClassName, "text-error")}
        >
          <>{t("priceSelect.savings")}:</>
          <Placeholder className="w-[4em]" loading={!part}>
            <PriceDisplay>
              {Number(savings) * Number(primaryPriceOption?.quantity)}
            </PriceDisplay>
          </Placeholder>
        </KeyValue>
      )}
    </>
  );
};
